"use client";

import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { EnhancedBlockConfig } from "@/app/(app)/components/_utils/component-data";
import { ImageIcon, Play } from "lucide-react";
import { Code } from "lucide-react";
import { Package } from "lucide-react";
import { usePathname } from "next/navigation";
import { siteConfig } from "@/config/site";

/**
 * ComponentBreadcrumb props
 * @typedef {Object} ComponentBreadcrumbProps
 * @property {EnhancedBlockConfig} component - The component configuration data
 * @property {string} [currentTab] - Optional current tab name (code, usage, images)
 */
interface ComponentBreadcrumbProps {
  component: EnhancedBlockConfig;
  currentTab?: string;
}

/**
 * Component Breadcrumb Navigation
 *
 * Displays a hierarchical navigation breadcrumb for component details pages.
 * Shows the path: Components > Library > Component > [Current Tab]
 *
 * The breadcrumb adapts based on whether the user is on the main component page
 * or on a sub-tab page (code, usage, images).
 *
 * @example
 * ```tsx
 * <ComponentBreadcrumb
 *   component={componentData}
 *   currentTab="code"
 * />
 * ```
 */
export function ComponentBreadcrumb({
  component,
  currentTab,
}: ComponentBreadcrumbProps) {
  const pathname = usePathname();
  const basePath = `/components/${component.library}/${component.name}`;

  // Tab definitions
  const tabs = [
    { id: "", label: "Preview", icon: Play, path: basePath },
    { id: "code", label: "Code", icon: Code, path: `${basePath}/code` },
    { id: "usage", label: "Usage", icon: Package, path: `${basePath}/usage` },
    {
      id: "images",
      label: "Images",
      icon: ImageIcon,
      path: `${basePath}/images`,
      disabled: !(
        component.availableImages && component.availableImages.length > 0
      ),
    },
  ];

  const activeTab =
    pathname === basePath ? "" : pathname.split("/").pop() || "";
  // Base paths
  const componentsPath = "/components";
  const libraryPath = `${componentsPath}/${component.library}`;
  const componentPath = `${libraryPath}/${component.name}`;

  // Determine if we're on a tab or main component page
  const isTabPage = !!currentTab;
  const tabTitle = currentTab
    ? currentTab.charAt(0).toUpperCase() + currentTab.slice(1)
    : "";

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link href="/">{siteConfig.name}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbSeparator />

        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link href={libraryPath}>{component.library}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbSeparator />

        <BreadcrumbItem>
          {isTabPage ? (
            <BreadcrumbLink asChild>
              <Link href={componentPath}>{component.name}</Link>
            </BreadcrumbLink>
          ) : (
            <span className="font-medium text-foreground">
              {component.name}
            </span>
          )}
        </BreadcrumbItem>

        {isTabPage && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <span className="font-medium text-foreground">{tabTitle}</span>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
